import { useRouter } from 'next/navigation';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setCurrentPrompt, resetActiveStudySlice, setSetName } from '@/lib/slices/study-slice';
import { ActivityData } from 'course-types';
import { QuizQuestionData } from 'quiz-types';
import AuroraUtilityService from '@/services/util/aurora-utililty-service';
import ActiveStudyCacheService from '@/services/storage/active-study-cache-service';

export const useFlashcardGeneration = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  // Build a topics string from the quiz questions to guide flashcard generation
  const buildTopicsFromQuiz = (questionSet: QuizQuestionData[]): string => {
    const questions = (questionSet || [])
      .map((q) => (q?.question || '').trim())
      .filter((q) => q.length > 0);

    if (questions.length === 0) return 'General review of recent quiz topics';

    // Join questions; cap the total length to avoid overly long prompts
    const joined = questions.join('; ');
    const maxLen = 800;
    const trimmed = joined.length > maxLen ? joined.slice(0, maxLen) : joined;
    return trimmed;
  };

  const handleGenerateFlashcards = async (questionSet?: QuizQuestionData[], quizName?: string) => {
    const topics = buildTopicsFromQuiz(questionSet || []);
    // Navigate first so the user lands on the flashcards page (which shows loading state)
    router.push('/student/study/flashcards');
    // Kick off generation (updates global study state and cache)
    await AuroraUtilityService.generateFlashcards(topics, dispatch);
    // Use the quiz name as the flashcard set title
    const derivedName = (quizName ?? '').trim() || 'Flashcards';
    dispatch(setSetName(derivedName));
    // Update session cache as well so it persists on refresh
    const studyCookie = ActiveStudyCacheService.getStudyStateCookie();
    if (studyCookie) {
      studyCookie.setName = derivedName;
      ActiveStudyCacheService.setStudyStateCookie(studyCookie);
    }
  };

  return {
    handleGenerateFlashcards,
  };
};
